import type { ReactNode } from 'react';

import { Document, Page, Text, View } from '@react-pdf/renderer';

import { basePdfStyles } from '@/_shared';

import { CompletedIcon, SentIcon, SignedIcon } from './icons';
import styles from './style';
import type { AuditTrailStamp } from './timestamps';

const SENDER_EMAIL = 'achieve.esign@achieve-test-prep.com';

type AuditTrailTemplateV3Props = {
  signerName: string;
  signerEmail: string;
  fileName: string;
  signedAt: AuditTrailStamp;
  sentAt: AuditTrailStamp;
  ipAddress: string;
};

export function AuditTrailTemplateV3({
  signerName,
  signerEmail,
  fileName,
  signedAt,
  sentAt,
  ipAddress,
}: AuditTrailTemplateV3Props) {
  const displayName = signerName.trim() || 'Signer';
  const identity = signerEmail ? `${displayName} (${signerEmail})` : displayName;

  return (
    <Document>
      <Page size="A4" orientation="portrait" wrap style={[basePdfStyles.page, styles.page]}>
        <Text style={styles.title}>Audit trail</Text>

        <View style={styles.card}>
          <Text style={styles.sectionTitle}>Details</Text>
          <DetailRow label="FILE NAME" value={fileName} />
          <DetailRow label="STATUS">
            <View style={styles.statusValue}>
              <View style={styles.statusDot} />
              <Text style={styles.statusText}>Signed</Text>
            </View>
          </DetailRow>
          <DetailRow label="STATUS TIMESTAMP">
            <Stamp value={signedAt} />
          </DetailRow>
          <DetailRow label="IP ADDRESS" value={ipAddress.trim() || '-'} last />
        </View>

        <View style={styles.card}>
          <Text style={styles.sectionTitle}>Activity</Text>
          <ActivityRow icon={<SentIcon />} label="SENT" stamp={sentAt}>
            <Text style={styles.bodyText}>
              {SENDER_EMAIL} <Text style={styles.emphasis}>sent</Text> a signature request to:
            </Text>
            <View style={styles.recipient}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bodyText}>{identity}</Text>
            </View>
          </ActivityRow>
          <ActivityRow icon={<SignedIcon />} label="SIGNED" stamp={signedAt}>
            <Text style={styles.bodyText}>
              <Text style={styles.emphasis}>Signed</Text> by {identity}
            </Text>
          </ActivityRow>
          <ActivityRow icon={<CompletedIcon />} label="COMPLETED" stamp={signedAt} last>
            <Text style={styles.bodyText}>
              This document has been signed by all signers and is <Text style={styles.emphasis}>complete</Text>
            </Text>
          </ActivityRow>
        </View>

        <Text style={styles.disclaimer}>
          The email address indicated above for each signer may be associated with a Google account, and may either be
          the primary email address or secondary email address associated with that account.
        </Text>

        <View style={styles.signatureBlock}>
          <Text style={styles.signatureLabel}>Signature</Text>
          <Text style={styles.signature}>{displayName}</Text>
        </View>
      </Page>
    </Document>
  );
}

function DetailRow({
  label,
  value,
  last = false,
  children,
}: {
  label: string;
  value?: string;
  last?: boolean;
  children?: ReactNode;
}) {
  return (
    <View style={last ? [styles.detailRow, styles.detailRowLast] : styles.detailRow}>
      <Text style={styles.detailLabel}>{label}</Text>
      {children ?? <Text style={styles.detailValue}>{value}</Text>}
    </View>
  );
}

function Stamp({ value, align = 'left' }: { value: AuditTrailStamp; align?: 'left' | 'right' }) {
  const textStyle = align === 'right' ? styles.stampText : styles.stampValue;

  return (
    <View style={align === 'right' ? styles.stampColumnRight : styles.stampColumn}>
      <Text style={textStyle}>{value.date}</Text>
      {!!value.time && (
        <View style={styles.stampTime}>
          <Text style={textStyle}>{value.time}</Text>
        </View>
      )}
    </View>
  );
}

function ActivityRow({
  icon,
  label,
  stamp,
  last = false,
  children,
}: {
  icon: ReactNode;
  label: string;
  stamp: AuditTrailStamp;
  last?: boolean;
  children: ReactNode;
}) {
  return (
    <View style={last ? [styles.activityRow, styles.detailRowLast] : styles.activityRow}>
      <View style={styles.activityIcon}>
        {icon}
        <Text style={styles.activityLabel}>{label}</Text>
      </View>
      <View style={styles.activityBody}>{children}</View>
      <View style={styles.stamp}>
        <Stamp value={stamp} align="right" />
      </View>
    </View>
  );
}
